/**
 * Bottle filters component
 */

import { Select, SearchBar, Checkbox } from '../common';
import { useAllDistilleries } from '../../hooks/useDistilleries';
import { useStorePicks } from '../../hooks/useBottles';
import { BOTTLE_TYPES, BOTTLE_STATUSES, BOTTLE_SIZES } from '../../utils/constants';
import './BottleFilters.css';

/**
 * Filter controls for bottle list
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Filter change handler
 * @returns {React.ReactElement} Filters element
 */
export default function BottleFilters({ filters, onFilterChange, showAdvanced = true }) {
  const { data: distilleries = [] } = useAllDistilleries();
  const { data: storePicks = [] } = useStorePicks();

  const statusArray = filters.status || [];

  const handleSearchChange = (value) => {
    onFilterChange({ ...filters, search: value });
  };

  const handleChange = (key) => (e) => {
    onFilterChange({ ...filters, [key]: e.target.value });
  };

  const handleStatusToggle = (status) => {
    const next = statusArray.includes(status)
      ? statusArray.filter(s => s !== status)
      : [...statusArray, status];
    onFilterChange({ ...filters, status: next });
  };

  const distilleryOptions = [
    { value: '', label: 'All Distilleries' },
    ...distilleries.map(d => ({ value: d.id, label: d.distillery_name }))
  ];

  return (
    <div className="bottle-filters">
      <SearchBar
        value={filters.search || ''}
        onChange={handleSearchChange}
        placeholder="Search bottles..."
      />

      {showAdvanced && (
        <div className="bottle-filters__selects">
          <Select
            label="Distillery"
            options={distilleryOptions}
            value={filters.distillery_id || ''}
            onChange={handleChange('distillery_id')}
            className="bottle-filters__select"
          />

          <Select
            label="Type"
            options={[{ value: '', label: 'All Types' }, ...BOTTLE_TYPES.map(t => ({ value: t, label: t }))]}
            value={filters.type || ''}
            onChange={handleChange('type')}
            className="bottle-filters__select"
          />

          <div className="bottle-filters__status-group">
            <span className="bottle-filters__status-label">Status</span>
            <div className="bottle-filters__status-checkboxes">
              {BOTTLE_STATUSES.map(status => (
                <Checkbox
                  key={status}
                  label={status}
                  checked={statusArray.includes(status)}
                  onChange={() => handleStatusToggle(status)}
                />
              ))}
            </div>
          </div>

          <Select
            label="Size"
            options={[{ value: '', label: 'All Sizes' }, ...BOTTLE_SIZES.map(s => ({ value: s, label: s }))]}
            value={filters.size || ''}
            onChange={handleChange('size')}
            className="bottle-filters__select"
          />

          <Select
            label="Store Pick"
            options={[{ value: '', label: 'All Stores' }, ...storePicks.map(s => ({ value: s, label: s }))]}
            value={filters.store_pick || ''}
            onChange={handleChange('store_pick')}
            className="bottle-filters__select"
          />
        </div>
      )}
    </div>
  );
}
